import type { DailySpend } from '@/api/dashboard'
import { ROUTES } from '@/routes/routes'
import { currentMonth } from '@/lib/transactionDateRangeFilter'
import { transactionsLink } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { SectionLink } from './SectionLink'

const LABEL_DAYS = [1, 8, 15, 22, 29]
// Bars top out below the chart's full height so the tallest one still has room for its amount.
const BAR_ROOM = 0.85

export function DailySpendingStrip({
  daily,
  daysInMonth,
  today,
  format,
  formatCompact,
  compact,
  className,
}: {
  daily: DailySpend[]
  daysInMonth: number
  today: number
  format: (n: number) => string
  formatCompact: (n: number) => string
  compact: boolean
  className?: string
}) {
  const max = Math.max(0, ...daily.map((d) => d.total))
  const spent = daily.reduce((sum, d) => sum + d.total, 0)
  const avg = daily.length > 0 ? spent / daily.length : 0
  const biggest = daily.find((d) => d.total === max)

  const summary =
    max > 0 && biggest
      ? compact
        ? `avg ${format(Math.round(avg))} / day`
        : `Avg ${format(Math.round(avg))} / day · biggest ${dayLabel(biggest.date)}, ${format(max)}`
      : 'No spending yet'

  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1
    const entry = daily[i]
    const past = day <= today && entry !== undefined
    const total = past ? Math.max(entry.total, 0) : 0
    // Net-refund days (≤ 0) still get a stub, so the day reads as "nothing spent", not missing.
    const height = total > 0 ? Math.max((total / max) * 100 * BAR_ROOM, 2) : 2
    const peak = total > 0 && total === max
    return { day, past, total, height, peak }
  })

  return (
    <section className={cn('flex min-h-0 flex-col', className)}>
      <SectionLink
        to={transactionsLink({ month: currentMonth() }, { back: ROUTES.APP.DASHBOARD })}
        label="Daily spending"
        aside={summary}
      />
      <div
        role="img"
        aria-label={`Spending per day this month. ${summary}`}
        className="relative mt-1 h-16 lg:h-auto lg:min-h-[100px] lg:flex-1"
      >
        <div className="absolute inset-0 flex items-end gap-[3px] lg:gap-1.5">
          {days.map((d) => {
            const key = d.peak || d.day === today
            return (
              <div key={d.day} className="relative flex h-full min-w-0 flex-1 items-end">
                {d.total > 0 && (
                  // Phone width can't fit 31 amounts, so only the biggest day and today keep theirs.
                  <span
                    className={cn(
                      'absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tabular-nums',
                      key
                        ? 'font-semibold text-[var(--fl-heading)]'
                        : 'hidden text-[var(--fl-text-2)] lg:block'
                    )}
                    style={{ bottom: `calc(${d.height}% + 3px)` }}
                  >
                    {formatCompact(d.total)}
                  </span>
                )}
                <div
                  className={cn(
                    'min-h-[3px] w-full rounded-t-[3px] rounded-b-[1px]',
                    key
                      ? 'bg-[var(--fl-expense)]'
                      : d.past
                        ? 'bg-[var(--fl-expense)] opacity-45'
                        : 'bg-[var(--fl-border)]'
                  )}
                  style={{ height: `${d.height}%` }}
                />
              </div>
            )
          })}
        </div>
        {max > 0 && (
          <div
            className="pointer-events-none absolute inset-x-0 border-t border-dashed border-[var(--fl-text-3)]"
            style={{ bottom: `${(avg / max) * 100 * BAR_ROOM}%` }}
          >
            <span className="absolute right-0 -translate-y-full bg-[var(--fl-bg)] pb-0.5 pl-1.5 text-[11px] text-[var(--fl-text-2)]">
              avg {format(Math.round(avg))}
            </span>
          </div>
        )}
      </div>
      <div className="mt-1.5 flex gap-[3px] lg:gap-1.5" aria-hidden="true">
        {days.map((d) => {
          const isToday = d.day === today
          const show = isToday || (LABEL_DAYS.includes(d.day) && Math.abs(d.day - today) > 2)
          return (
            <span
              key={d.day}
              className={cn(
                'flex-1 overflow-visible whitespace-nowrap text-center text-[11px]',
                isToday ? 'font-semibold text-[var(--fl-heading)]' : 'text-[var(--fl-text-3)]'
              )}
            >
              {show ? (isToday ? 'Today' : d.day) : ''}
            </span>
          )
        })}
      </div>
    </section>
  )
}

function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}
